import { initializeApp } from 'firebase/app';
import {
  initializeAuth,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getFirebaseConfig, hasConfig } from './appConfig';

// 接続設定が入力されるまでは初期化しない。
// 設定画面で保存するとページが読み込み直され、そこで初期化される。
let app = null;
let auth = null;
let db = null;

if (hasConfig()) {
  try {
    app = initializeApp(getFirebaseConfig());

    /**
     * 認証の保存先。
     *
     * 何も指定しないと IndexedDB が最優先になるが、端末によっては
     * それが開けず「Database is closing」で止まる。
     * localStorage を先に置いて、駄目なときだけ下へ降りる。
     */
    auth = initializeAuth(app, {
      persistence: [browserLocalPersistence, browserSessionPersistence, inMemoryPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });

    /**
     * Firestore のキャッシュ。
     *
     * いまは端末に残す形（IndexedDB）をやめて、その場のメモリだけにしてある。
     * 残す形だと、壊れたときに「文書が無い」と嘘の答えを返し、
     * しかもエラーにならないため、原因を切り分けにくい。
     *
     * 影響は、通信できないときに過去の記録が見られないことだけ。
     * 記録そのものは端末のlocalStorageにも残るので、失われない。
     */
    db = initializeFirestore(app, { localCache: memoryLocalCache() });
  } catch (e) {
    console.error('Firebaseの初期化に失敗:', e);
  }
}

export { auth, db };

export const isFirebaseReady = () => auth !== null && db !== null;

const provider = new GoogleAuthProvider();

/** 別窓が開けない端末では、画面を移動する方式に切り替える */
const POPUP_FALLBACK = new Set([
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

export const signInWithGoogle = async () => {
  if (!auth) throw new Error('接続設定が未入力です');
  try {
    return await signInWithPopup(auth, provider);
  } catch (e) {
    if (!POPUP_FALLBACK.has(e.code)) throw e;
    // 戻ってきたときに onAuthStateChanged が呼ばれるので、ここでは待たない
    await signInWithRedirect(auth, provider);
    return null;
  }
};

export const signOutUser = () => {
  if (!auth) return Promise.resolve();
  return signOut(auth);
};

/** ログイン状態の変化を監視する。戻り値を呼ぶと監視を解除できる */
export const onAuthChange = (cb) => {
  if (!auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
};
